import type { FieldErrors, FieldValues, Resolver } from "react-hook-form";
import type { z } from "zod";

/**
 * Binds a Zod schema to React Hook Form.
 *
 * `forms-and-validation.md` names `zodResolver`, and the package that ships it is not a
 * dependency of this repo. Twenty lines of glue do not justify one, so the contract is met
 * here: the schema's parsed output becomes the submitted values, the first issue per field
 * wins, and a nested path lands where `setError` would put it.
 */
export function zodResolver<TIn extends FieldValues, TOut extends FieldValues>(
  schema: z.ZodType<TOut, TIn>,
): Resolver<TIn, unknown, TOut> {
  return async (values) => {
    const result = await schema.safeParseAsync(values);
    if (result.success) return { values: result.data, errors: {} };

    const errors: Record<string, unknown> = {};
    for (const issue of result.error.issues) {
      const path = issue.path.map(String);
      if (path.length > 0) place(errors, path, { type: issue.code, message: issue.message });
    }
    return { values: {}, errors: errors as FieldErrors<TIn> };
  };
}

function place(
  target: Record<string, unknown>,
  path: string[],
  leaf: { type: string; message: string },
): void {
  const [head, ...rest] = path;
  if (head === undefined) return;

  if (rest.length === 0) {
    if (!(head in target)) target[head] = leaf;
    return;
  }

  const existing = target[head];
  // The branch is already an error leaf — the first issue for a field wins.
  if (existing !== undefined && (typeof existing !== "object" || existing === null || "message" in existing)) {
    return;
  }
  const branch = (existing as Record<string, unknown> | undefined) ?? {};
  target[head] = branch;
  place(branch, rest, leaf);
}
